'use client';
import React, { useEffect, useRef, FC, useState } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
}

const Index: FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // On desktop, render immediately
      if (!mobile) {
        setShouldRender(true);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // On mobile, defer rendering with intersection observer
  useEffect(() => {
    if (!isMobile || shouldRender) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setShouldRender(true), 2000);
          observer.disconnect();
        }
      },
      { threshold: 0.01, rootMargin: '100px' },
    );

    observer.observe(canvas);
    return () => observer.disconnect();
  }, [isMobile, shouldRender]);

  // Intersection observer to detect visibility
  useEffect(() => {
    if (!shouldRender) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.01 },
    );

    observer.observe(canvas);
    return () => observer.disconnect();
  }, [shouldRender]);

  useEffect(() => {
    if (!shouldRender) {
      return;
    }
    if (typeof window === 'undefined' || !isVisible) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const context = canvas.getContext('2d', {
      alpha: true,
      desynchronized: true, // Better performance
    });
    if (!context) {
      return;
    }

    let width: number;
    let height: number;
    const particles: Particle[] = [];

    const particleCount = isMobile ? 20 : 50;
    let animationFrameId: number;
    let mouseX = 0;
    let mouseY = 0;
    let lastFrameTime = 0;
    const targetFPS = 30; // Throttle to 30 FPS
    const frameInterval = 1000 / targetFPS;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width;
      canvas.height = height;
    };

    const createParticle = (): Particle => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 2,
      vy: (Math.random() - 0.5) * 2,
      size: Math.random() * 3 + 1,
      opacity: Math.random() * 0.5 + 0.3,
    });

    const initParticles = () => {
      particles.length = 0;
      for (let i = 0; i < particleCount; i++) {
        particles.push(createParticle());
      }
    };

    const updateParticles = () => {
      particles.forEach((particle) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Wrap around edges
        if (particle.x < 0) {
          particle.x = width;
        }
        if (particle.x > width) {
          particle.x = 0;
        }
        if (particle.y < 0) {
          particle.y = height;
        }
        if (particle.y > height) {
          particle.y = 0;
        }
      });
    };

    const drawParticles = () => {
      context.clearRect(0, 0, width, height);

      // Draw background gradient
      const gradient = context.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, '#0E1010');
      gradient.addColorStop(1, '#0F423A');
      context.fillStyle = gradient;
      context.fillRect(0, 0, width, height);

      // Draw particles
      particles.forEach((particle) => {
        context.beginPath();
        context.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        context.fillStyle = `rgba(22, 125, 106, ${particle.opacity})`;
        context.fill();
      });

      // Draw connections
      particles.forEach((particle, i) => {
        particles.slice(i + 1).forEach((otherParticle) => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            const opacity = ((150 - distance) / 150) * 0.4;
            context.beginPath();
            context.moveTo(particle.x, particle.y);
            context.lineTo(otherParticle.x, otherParticle.y);
            context.strokeStyle = `rgba(22, 125, 106, ${opacity})`;
            context.lineWidth = 1;
            context.stroke();
          }
        });
      });
    };

    const handleMouseMove = (event: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseX = event.clientX - rect.left;
      mouseY = event.clientY - rect.top;

      // Repulse effect
      particles.forEach((particle) => {
        const dx = particle.x - mouseX;
        const dy = particle.y - mouseY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 200) {
          const force = (200 - distance) / 200;
          particle.vx += (dx / distance) * force * 0.5;
          particle.vy += (dy / distance) * force * 0.5;
        }
      });
    };

    const handleClick = () => {
      // Add particles on click
      for (let i = 0; i < 4; i++) {
        particles.push({
          x: mouseX + (Math.random() - 0.5) * 20,
          y: mouseY + (Math.random() - 0.5) * 20,
          vx: (Math.random() - 0.5) * 4,
          vy: (Math.random() - 0.5) * 4,
          size: Math.random() * 3 + 1,
          opacity: Math.random() * 0.5 + 0.3,
        });
      }
    };

    const animate = (currentTime: number) => {
      // Throttle to target FPS
      const elapsed = currentTime - lastFrameTime;

      if (elapsed > frameInterval) {
        lastFrameTime = currentTime - (elapsed % frameInterval);
        updateParticles();
        drawParticles();
      }

      if (isVisible && !document.hidden) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    // Defer initialization using requestIdleCallback
    const init = () => {
      resize();
      initParticles();
      // Add extra delay before starting animation on mobile
      const startAnimation = () => {
        animate(performance.now());
      };
      const animationDelay = isMobile ? 1000 : 0;
      setTimeout(startAnimation, animationDelay);
    };

    if ('requestIdleCallback' in window) {
      const idleTimeout = isMobile ? 3000 : 1000;
      const w = window as Window & {
        requestIdleCallback: (
          cb: IdleRequestCallback,
          options?: IdleRequestOptions,
        ) => number;
      };
      w.requestIdleCallback(init, { timeout: idleTimeout });
    } else {
      const fallbackDelay = isMobile ? 2000 : 100;
      setTimeout(init, fallbackDelay);
    }

    // Handle visibility change to pause/resume
    const handleVisibilityChange = () => {
      if (!document.hidden && isVisible) {
        lastFrameTime = performance.now();
        animate(lastFrameTime);
      } else {
        cancelAnimationFrame(animationFrameId);
      }
    };

    window.addEventListener('resize', resize, { passive: true });
    canvas.addEventListener('mousemove', handleMouseMove, { passive: true });
    canvas.addEventListener('click', handleClick);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('click', handleClick);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isVisible, isMobile, shouldRender]);

  return (
    <canvas
      ref={canvasRef}
      className="particles-canvas"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'block',
        background: 'transparent',
        zIndex: -1,
        pointerEvents: 'none',
        // Hide canvas until ready to prevent hydration mismatch
        opacity: shouldRender ? 1 : 0,
      }}
    />
  );
};

export default Index;
